import type { NextRequest } from 'next/server';
import {
  extractUserEmailFromToken,
  getClientIP,
  getUserAgent,
} from './auth-utils.js';
import {
  getGoogleTokensFromSupabase,
  getMsftTokensFromSupabase,
  logAuditEvent,
} from './supabase.js';
import { createProfessionalEmailCalendarEventViaHub } from './professional-email-calendar-bridge.js';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface CalendarEventRequest {
  summary: string;
  description?: string;
  start: string;
  end: string;
  location?: string;
  attendees?: string[];
  isSecondAccount?: boolean;
}

export interface CalendarEventResponse {
  id: string;
  summary?: string;
  subject?: string;
  [key: string]: any;
}

export interface CalendarCreationResult {
  event: CalendarEventResponse;
  auditData: {
    eventId: string;
    eventSummary: string;
    isSecondAccount: boolean;
    isOnlineMeeting: boolean;
    attendeesCount: number;
  };
}

export type CalendarProvider = 'google' | 'microsoft' | 'professional-email';

// ============================================================================
// SHARED VALIDATION AND AUTHENTICATION
// ============================================================================

export async function validateCalendarCreationRequest(
  request: NextRequest,
  eventData: CalendarEventRequest
): Promise<{ userEmail: string; validatedData: CalendarEventRequest }> {
  // Extract user email from JWT token
  const userEmail = await extractUserEmailFromToken(request);
  if (!userEmail) {
    throw new Error('No valid authorization token found');
  }

  // Validate required parameters
  if (!eventData.summary || !eventData.start || !eventData.end) {
    throw new Error('Missing required fields: summary, start, end');
  }

  return { userEmail, validatedData: eventData };
}

export async function getCalendarTokens(
  provider: CalendarProvider,
  userEmail: string,
  isSecondAccount: boolean = false
): Promise<string> {
  let tokenData;
  
  if (provider === 'google') {
    tokenData = await getGoogleTokensFromSupabase(userEmail);
    if (!tokenData) {
      throw new Error('No Google tokens found for user');
    }
  } else {
    tokenData = await getMsftTokensFromSupabase(userEmail);
    if (!tokenData) {
      throw new Error('No Microsoft tokens found for user');
    }
  }

  const accessToken = isSecondAccount ? tokenData.access_token2 : tokenData.access_token;
  if (!accessToken) {
    throw new Error(`No access token found for ${isSecondAccount ? 'second' : 'main'} account`);
  }

  return accessToken;
}

// ============================================================================
// GOOGLE CALENDAR FUNCTIONS
// ============================================================================

export function transformToGoogleEventFormat(eventData: CalendarEventRequest): any {
  const isOnlineMeeting = eventData.location === "ONLINE";
  
  const eventDetails: any = {
    summary: eventData.summary,
    description: eventData.description || '',
    start: {
      dateTime: eventData.start,
      timeZone: "America/Chicago"
    },
    end: {
      dateTime: eventData.end,
      timeZone: "America/Chicago"
    },
    location: isOnlineMeeting ? undefined : eventData.location,
    attendees: eventData.attendees ? eventData.attendees.map((email: string) => ({ email })) : []
  };

  // If it's an online meeting, add the necessary properties
  if (isOnlineMeeting) {
    eventDetails.conferenceData = {
      createRequest: {
        requestId: `meet-${Date.now()}`,
        conferenceSolutionKey: { type: "hangoutsMeet" }
      }
    };
  }

  return eventDetails;
}

export async function createGoogleCalendarEvent(
  accessToken: string,
  eventData: CalendarEventRequest
): Promise<CalendarEventResponse> {
  console.log(`[Audit Log] Creating Google Calendar event: ${eventData.summary}`);
  
  const eventDetails = transformToGoogleEventFormat(eventData);
  const isOnlineMeeting = eventData.location === "ONLINE";
  
  const url = isOnlineMeeting 
    ? "https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1"
    : "https://www.googleapis.com/calendar/v3/calendars/primary/events";

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken.trim()}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(eventDetails)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to create Google Calendar event: ${response.statusText} - ${JSON.stringify(errorData)}`);
  }

  return await response.json();
}

// ============================================================================
// MICROSOFT CALENDAR FUNCTIONS
// ============================================================================

export function transformToMicrosoftEventFormat(eventData: CalendarEventRequest): any {
  const isOnlineMeeting = eventData.location === "ONLINE";
  
  const eventDetails: any = {
    subject: eventData.summary,
    body: {
      contentType: "text",
      content: eventData.description || ''
    },
    start: {
      dateTime: eventData.start,
      timeZone: "America/Chicago"
    },
    end: {
      dateTime: eventData.end,
      timeZone: "America/Chicago"
    },
    location: isOnlineMeeting ? undefined : {
      displayName: eventData.location
    },
    attendees: eventData.attendees ? eventData.attendees.map((email: string) => ({ 
      emailAddress: { address: email } 
    })) : []
  };

  // If it's an online meeting, add the necessary properties
  if (isOnlineMeeting) {
    eventDetails.isOnlineMeeting = true;
    eventDetails.onlineMeetingProvider = "teamsForBusiness";
  }

  return eventDetails;
}

export async function createMicrosoftCalendarEvent(
  accessToken: string,
  eventData: CalendarEventRequest
): Promise<CalendarEventResponse> {
  console.log(`[Audit Log] Creating Microsoft Calendar event: ${eventData.summary}`);
  
  const eventDetails = transformToMicrosoftEventFormat(eventData);

  const response = await fetch("https://graph.microsoft.com/v1.0/me/events", {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken.trim()}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(eventDetails)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to create Microsoft Calendar event: ${response.statusText} - ${JSON.stringify(errorData)}`);
  }

  return await response.json();
}

// ============================================================================
// WTC EMAIL (PROFESSIONAL EMAIL) CALENDAR FUNCTIONS
// ============================================================================

export async function createProfessionalEmailCalendarEvent(
  userEmail: string,
  eventData: CalendarEventRequest
): Promise<CalendarEventResponse> {
  console.log(`[Audit Log] Creating WTC Email calendar event: ${eventData.summary}`);

  const isOnlineMeeting = eventData.location === 'ONLINE';

  const result = await createProfessionalEmailCalendarEventViaHub({
    hostEmail: userEmail,
    title: eventData.summary,
    startUtc: eventData.start,
    endUtc: eventData.end,
    description: eventData.description,
    location: isOnlineMeeting ? undefined : eventData.location,
    attendeeEmails: eventData.attendees,
    timezone: 'America/Chicago',
  });

  if (!result.ok) {
    if (result.reason === 'not_configured') {
      throw new Error('WTC Email calendar is not connected');
    }
    throw new Error(`Failed to create WTC Email calendar event: ${result.reason}`);
  }

  const event = result.event;
  return {
    id: event.eventId,
    summary: event.title,
    start: { dateTime: event.startUtc, timeZone: event.timezone },
    end: { dateTime: event.endUtc, timeZone: event.timezone },
  };
}

// ============================================================================
// MAIN CALENDAR CREATION FUNCTION
// ============================================================================

export async function createCalendarEvent(
  request: NextRequest,
  provider: CalendarProvider,
  eventData: CalendarEventRequest
): Promise<CalendarCreationResult> {
  console.log(`=== ${provider.toUpperCase()} CALENDAR CREATE REQUEST ===`);
  console.log("Summary:", eventData.summary);
  console.log("Start:", eventData.start);
  console.log("End:", eventData.end);
  console.log("Location:", eventData.location);
  console.log("Attendees:", eventData.attendees);
  console.log("Is Second Account:", eventData.isSecondAccount);

  // Validate request and get user email
  const { userEmail, validatedData } = await validateCalendarCreationRequest(request, eventData);

  // Create the event based on provider. WTC Email uses the Hub bridge (no OAuth tokens).
  let createdEvent: CalendarEventResponse;
  if (provider === 'professional-email') {
    createdEvent = await createProfessionalEmailCalendarEvent(userEmail, validatedData);
  } else {
    const accessToken = await getCalendarTokens(provider, userEmail, eventData.isSecondAccount);
    if (provider === 'google') {
      createdEvent = await createGoogleCalendarEvent(accessToken, validatedData);
    } else {
      createdEvent = await createMicrosoftCalendarEvent(accessToken, validatedData);
    }
  }

  // Prepare audit data
  const isOnlineMeeting = eventData.location === "ONLINE";
  const auditData = {
    eventId: createdEvent.id,
    eventSummary: createdEvent.summary || createdEvent.subject || eventData.summary,
    isSecondAccount: eventData.isSecondAccount || false,
    isOnlineMeeting,
    attendeesCount: eventData.attendees?.length || 0
  };

  // Log audit event
  await logAuditEvent(
    userEmail,
    `${provider}_calendar_event_created`,
    provider,
    auditData,
    getClientIP(request) || undefined,
    getUserAgent(request) || undefined
  );

  return { event: createdEvent, auditData };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getCalendarCreationHeaders(status: number = 200) {
  const headers: Record<string, string> = {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, OPTIONS",
    "Cache-Control": "private, max-age=300"
  };

  return headers;
}

export function createErrorResponse(error: any, status: number = 500) {
  return {
    error: "Failed to create calendar event",
    message: error.message || "An unexpected error occurred"
  };
}
